// ============================================================================
// PRIX PAR DÉCLINAISON — les dérogations au tarif commun.
//
// ⚠️ LA SÉMANTIQUE TIENT DANS LE SOUS-TITRE DE LA MAQUETTE : « Case vide = prix commun ; un montant
// saisi déroge. » Une case n'est donc PAS un champ « prix de cette taille » qu'il faudrait remplir —
// c'est une EXCEPTION qu'on pose, et qu'on retire en vidant la case. Le placeholder porte le prix
// commun pour que la différence se voie sans rien lire.
//
// ⚠️ CE BLOC N'ENREGISTRE RIEN : il RAPPORTE ses saisies à la fiche (`onEdits`), qui les tient dans la
// MÊME barre Annuler/Enregistrer que le reste — cohérence exigée par l'utilisateur (2026-08-24 : le
// bouton local sous le tableau divergeait du reste de la fiche, et un prix partait sans confirmation).
// La première doctrine (« un bouton unique masquerait deux écritures indépendantes ») est levée par le
// mécanisme des témoins du Pied : chaque écriture a le sien, un échec partiel se dit, la saisie
// refusée reste dans la barre. La CONFIRMATION avant d'écrire des prix vit dans la fiche (`confirm`
// stylé — c'est de l'argent, et le journal est définitif).
//
// ⚠️ L'OUTIL DE POURCENTAGE MONTRE AVANT D'AGIR. Il ne touche pas la base : il REMPLIT la matrice, que
// l'opérateur relit et corrige avant d'enregistrer. Appliquer directement en base ferait d'un clic sur
// « +5 % » une écriture sur douze déclinaisons, sans retour possible — le journal est append-only.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { formatMoney, parseMinor, toInputValue } from '@orgazone/money-format';
import { mulDivRound } from '@orgazone/core-money';
import { SCAN_ZONE_ATTR, Trans } from '@orgazone/ui-web';
import { api, type ArticleVariantSet } from '../api';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';

/**
 * Traduit les saisies en PRIX À ÉCRIRE — la même règle pour la fiche (envoi) et ce bloc (affichage).
 * ⚠️ UNE CASE VIDÉE REMET AU TARIF COMMUN — elle n'efface pas le prix (un prix nul rendrait l'article
 * gratuit, un prix absent le rendrait invendable). `invalides` = les cases illisibles, qui BLOQUENT.
 */
export function preparerPrixAEnvoyer(
  saisie: Record<string, string>, prixCommunCents: number | null,
): { prices: { gencode: string; priceCents: number }[]; invalides: string[] } {
  const prices: { gencode: string; priceCents: number }[] = [];
  const invalides: string[] = [];
  for (const [gencode, texte] of Object.entries(saisie)) {
    const lu = parseMinor(texte, { scale: 2 });
    if (lu.state === 'empty') { if (prixCommunCents !== null) prices.push({ gencode, priceCents: prixCommunCents }); continue; }
    if (lu.state === 'ok') prices.push({ gencode, priceCents: lu.minor });
    else invalides.push(gencode);
  }
  return { prices, invalides };
}

/** Ce qui remplace une couleur ou une taille absente — jamais une chaîne vide, qui ne se voit pas. */
const SANS = '—';

export interface PrixDeclinaisonsProps {
  modelKey: string;
  /** Le tarif commun, en centimes — sert de placeholder ET de base au calcul en pourcentage. */
  prixCommunCents: number | null;
  editable: boolean;
  /** Les saisies en attente, par gencode — TENUES PAR LA FICHE (barre commune). Absente = base. */
  edits: Record<string, string>;
  /** Remplace la carte entière (saisie de case, outil %, duplication de rangée). */
  onEdits: (suivant: Record<string, string>) => void;
  /** Incrémenté par la fiche après un enregistrement — relit les prix SERVIS. */
  version: number;
}

/**
 * LE FACTEUR D'UNE HAUSSE OU D'UNE BAISSE, en points de base — ou `null` si la saisie n'est pas
 * applicable.
 *
 * ⚠️ UNE BAISSE NE PEUT PAS ATTEINDRE 100 % (audit 2026-08-25) : à 100 %, l'outil remplissait TOUTE
 * la matrice de 0,00 € — et le serveur l'acceptait, donc des articles partaient gratuits en caisse.
 * Au-delà de 100 %, le prix devenait négatif.
 */
export function facteurPourcentBp(sens: 'hausse' | 'baisse', pourcent: string): number | null {
  const p = parseMinor(pourcent, { scale: 2 });
  if (p.state !== 'ok' || p.minor <= 0 || (sens === 'baisse' && p.minor >= 10_000)) return null;
  return sens === 'hausse' ? 10_000 + p.minor : 10_000 - p.minor;
}

/**
 * LE PRIX APRÈS APPLICATION — la seule définition, employée par l'aperçu ET par l'écriture.
 *
 * 🔴 ELLES ÉTAIENT DEUX (corrigé le 2026-09-09). L'écriture déléguait déjà à `mulDivRound`, sous un
 * commentaire qui interdisait explicitement « un produit flottant divisé à la main » ; l'aperçu, 360
 * lignes plus bas, faisait exactement cela. Les deux résultats coïncidaient — mesuré sur 2,4 millions
 * de combinaisons, zéro écart — mais rien ne les tenait ensemble : une correction de l'arrondi du
 * cœur aurait fait annoncer à l'écran un prix que l'écriture n'aurait pas produit.
 *
 * ⚠️ L'ARRONDI APPARTIENT AU CŒUR, PAS À L'ÉCRAN : c'est de l'argent qui part en base et descend aux
 * caisses.
 */
export function prixApresPourcentCents(baseCents: number, facteurBp: number): number {
  return mulDivRound(baseCents, facteurBp, 10_000);
}

export function PrixDeclinaisons({ modelKey, prixCommunCents, editable, edits, onEdits, version }: PrixDeclinaisonsProps) {
  const [jeu, setJeu] = useState<ArticleVariantSet | null>(null);
  const [echecLecture, setEchecLecture] = useState<string | null>(null);
  const saisie = edits;
  const [outilOuvert, setOutilOuvert] = useState(false);
  const champPourcent = useRef<HTMLInputElement | null>(null);
  // Le champ prend le focus a l ouverture : c est la seule saisie de cet outil.
  useEffect(() => { if (outilOuvert) champPourcent.current?.focus(); }, [outilOuvert]);
  const [sens, setSens] = useState<'hausse' | 'baisse'>('hausse');
  const [pourcent, setPourcent] = useState('');

  useEffect(() => {
    let vivant = true;
    api.articleVariants(modelKey)
      .then((r) => { if (vivant) { setJeu(r); setEchecLecture(null); } })
      .catch((e: unknown) => { if (vivant) setEchecLecture(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey, version]);

  const grid = useMemo(() => {
    const rows = jeu?.rows ?? [];
    const vues = [...new Set(rows.map((v) => v.size ?? SANS))];
    const declarees = (jeu?.sizeOrder ?? []).filter((size) => vues.includes(size));
    const sizes = [...declarees, ...vues.filter((size) => !declarees.includes(size))];
    const couleurs = [...new Set(rows.map((v) => v.color ?? SANS))];
    const par = new Map(rows.map((v) => [`${v.color ?? SANS}|${v.size ?? SANS}`, v]));
    return { sizes, couleurs, par, rows };
  }, [jeu]);

  if (echecLecture) {
    return (
      <p role="alert" style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
        {t('Les déclinaisons n’ont pas pu être lues : {echecLecture}', { echecLecture })}
      </p>
    );
  }
  if (!jeu) return <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture des déclinaisons…')}</p>;
  if (grid.rows.length === 0) return null;

  /** Le prix EFFECTIF d'une déclinaison : la saisie en cours, sinon ce qu'elle a en base. */
  const effectif = (gencode: string | null, prixBase: number): number | null => {
    if (gencode && gencode in saisie) {
      const lu = parseMinor(saisie[gencode]!, { scale: 2 });
      return lu.state === 'ok' ? lu.minor : null;
    }
    return prixBase;
  };

  /** Une déclinaison DÉROGE si son prix effectif diffère du tarif commun. */
  const deroge = (gencode: string | null, prixBase: number): boolean => {
    const p = effectif(gencode, prixBase);
    return p !== null && prixCommunCents !== null && p !== prixCommunCents;
  };

  const nbDerogations = grid.rows.filter((v) => deroge(v.gencode, v.priceCents)).length;
  const touche = Object.keys(saisie).length > 0;

  /** Les saisies mal formées — elles bloquent l'enregistrement, et le disent case par case. */
  const invalides = Object.entries(saisie)
    .filter(([, saisi]) => saisi.trim() !== '' && parseMinor(saisi, { scale: 2 }).state === 'rejected')
    .map(([g]) => g);

  /**
   * REMPLIT la matrice depuis un pourcentage — sans rien écrire en base.
   * ⚠️ LE CALCUL PART DU PRIX EFFECTIF de chaque déclinaison, pas du tarif commun : appliquer « +5 % »
   * à une taille qui dérogeait déjà doit majorer SON prix, sinon l’outil effacerait la dérogation en
   * prétendant l'augmenter.
   */
  const appliquerPourcent = () => {
    const facteurBp = facteurPourcentBp(sens, pourcent);
    if (facteurBp === null) return; // la borne se dit aussi à l'écran, juste en dessous
    const suivant: Record<string, string> = { ...saisie };
    for (const v of grid.rows) {
      if (!v.gencode) continue; // sans gencode, aucune écriture ne peut l'atteindre
      const base = effectif(v.gencode, v.priceCents);
      if (base === null) continue;
      suivant[v.gencode] = toInputValue(prixApresPourcentCents(base, facteurBp), 2);
    }
    onEdits(suivant);
    setOutilOuvert(false);
    setPourcent('');
  };

  /** Le motif qui empêche d'appliquer — `null` quand la saisie est bonne. ⚠️ Il se DIT : un bouton qui
   *  ne réagit pas laisse croire à une panne, là où seule la valeur est hors bornes (§23.0). */
  const pourcentRefuse = ((): string | null => {
    const p = parseMinor(pourcent, { scale: 2 });
    if (pourcent.trim() === '') return null;
    if (p.state !== 'ok' || p.minor <= 0) return t('Saisissez un pourcentage supérieur à zéro.');
    if (sens === 'baisse' && p.minor >= 10_000) return t('Une baisse de 100 % mettrait tous les prix à 0,00 € — saisissez moins de 100 %.');
    return null;
  })();

  /** REMET tout au tarif commun — en remplissant la matrice, pas en écrivant. */
  const toutAuCommun = () => {
    if (prixCommunCents === null) return;
    const suivant: Record<string, string> = { ...saisie };
    for (const v of grid.rows) if (v.gencode) suivant[v.gencode] = toInputValue(prixCommunCents, 2);
    onEdits(suivant);
  };

  return (
    <section style={{ minWidth: 0, marginTop: 30, paddingTop: 24, borderTop: '1.5px solid var(--ink-200)' }}>
      {/* ⚠️ LA VUE QUI MANIPULE DE L’ARGENT AVAIT PERDU L’AVEU (correctif d’audit 2026-08-25). La route
          rend `truncated` et `limit` précisément pour ne pas mentir, et le dépliage des déclinaisons
          les affiche déjà — ce composant ne lisait que `rows`. Sur un modèle à 468 déclinaisons, la
          note du bas annonçait « 3 dérogent au prix commun » sur 200 lignes lues, et « Tout remettre au
          prix commun » comme l’outil de pourcentage n’écrivaient que sur les 200 visibles. */}
      {jeu.truncated && (
        <p role="status" style={{ margin: '0 0 12px', padding: '6px 10px', borderRadius: 6, background: 'var(--amber-bg)', color: 'var(--ink-800)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
          <Trans k='Ce modèle porte plus de {declinaisons} : seules les {n} premières sont affichées. Le compte de dérogations et les outils ci-dessous <1>ne portent que sur elles</1> — des coloris entiers peuvent manquer.' vars={{ declinaisons: t('{n} déclinaison', { n: jeu.limit }), n: jeu.limit }} parts={[(x) => <strong>{x}</strong>]} /></p>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Prix par déclinaison')}</div>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
            
            {t('Case vide = prix commun ; un montant saisi déroge.')}
          </p>
        </div>
        {editable && (
          <button
            type="button"
            onClick={() => setOutilOuvert((v) => !v)}
            style={{
              height: 36, padding: '0 13px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
              border: '1px solid var(--violet-fg)', background: 'var(--paper)', color: 'var(--violet-fg)',
              fontSize: '12.5px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {/* L'étiquette de prix barrée d'un trait — le pictogramme de la maquette (l.3737). */}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M19 5L5 19M6.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM17.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
            </svg>
            
            {t('Ajuster les prix')}
          </button>
        )}
        {editable && nbDerogations > 0 && prixCommunCents !== null && (
          <button
            type="button"
            onClick={toutAuCommun}
            style={{
              height: 36, padding: '0 13px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
              border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)',
              fontSize: '12.5px', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap',
            }}
          >
            
            {t('Tout remettre au prix commun')}
          </button>
        )}
      </div>

      {/* ⚠️ L'OUTIL MONTRE AVANT D'AGIR : il remplit la matrice, il n'écrit pas. L'aperçu porte sur un
          prix RÉEL du modèle, pas sur un exemple — sinon il rassure sans rien prouver. */}
      {outilOuvert && editable && (
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12,
          padding: '13px 15px', border: '1px solid var(--violet-fg)', borderRadius: 'var(--r-sm)',
          background: 'var(--violet-bg)',
        }}>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--violet-fg)', marginBottom: 5 }}>{t('Sens')}</span>
            {/* ⚠️ UNE PISTE, PAS DEUX BOUTONS CÔTE À CÔTE (maquette l.3742) : c'est la piste qui dit que
                les deux options s'excluent. Posées nues sur le fond violet, elles se lisaient comme deux
                actions indépendantes — donc cumulables. */}
            <div style={{
              display: 'flex', padding: 3, gap: 3, borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', border: '1px solid var(--ink-200)',
            }}>
              {(['hausse', 'baisse'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={sens === s}
                  onClick={() => setSens(s)}
                  style={{
                    height: 30, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)', cursor: 'pointer',
                    fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap',
                    // Dans une piste, l'option au repos est TRANSPARENTE : c'est la piste qui porte le fond.
                    background: sens === s ? 'var(--violet-fg)' : 'transparent',
                    color: sens === s ? '#fff' : 'var(--ink-700)',
                  }}
                >
                  {s === 'hausse' ? t('Augmenter') : t('Diminuer')}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--violet-fg)', marginBottom: 5 }}>{t('Pourcentage')}</span>
            {/* ⚠️ LE « % » EST DEHORS, PAS DANS LE CHAMP (maquette l.3750) : dedans, il faudrait
                l'effacer pour retaper — ou le retrouver dans la valeur soumise. L'unité se montre,
                elle ne se saisit pas. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <input
                // ⚠️ LE FOCUS ENTRE ICI À L'OUVERTURE (2026-08-25) : on n'ouvre cet outil que pour taper
                // un pourcentage. Sans lui, il fallait un clic de plus, et le « 5 » suggéré se lisait
                // comme une valeur déjà saisie qu'on n'arrivait pas à effacer.
                ref={champPourcent}
                value={pourcent}
                onChange={(e) => setPourcent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); appliquerPourcent(); } }}
                inputMode="decimal"
                // ⚠️ `oz-field` POUR SON SEUL `::placeholder` : sans la recette, le « 5 » suggéré
                // héritait de la couleur et de la graisse du champ — il avait donc exactement l'aspect
                // d'une valeur SAISIE, et rien à l'écran ne distinguait « vide » de « 5 » (§23.0).
                className="oz-field"
                placeholder="5"
                aria-label={t('Pourcentage à appliquer')}
                style={{
                  width: 84, height: 36, padding: '0 10px', boxSizing: 'border-box',
                  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', fontWeight: 700,
                  textAlign: 'right', outline: 'none', fontVariantNumeric: 'tabular-nums',
                }}
              />
              <span aria-hidden style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-700)' }}>%</span>
            </span>
          </div>
          {pourcentRefuse === null
            ? <Apercu prixCommunCents={prixCommunCents} sens={sens} pourcent={pourcent} />
            : <span role="alert" style={{ fontSize: 'var(--t-body)', color: 'var(--rose-fg)', flex: 1, minWidth: 180 }}>{pourcentRefuse}</span>}
          <button
            type="button"
            onClick={appliquerPourcent}
            disabled={pourcentRefuse !== null}
            title={pourcentRefuse ?? undefined}
            style={{
              height: 36, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
              cursor: pourcentRefuse === null ? 'pointer' : 'not-allowed',
              background: 'var(--violet-fg)', color: 'var(--paper)', opacity: pourcentRefuse === null ? 1 : 0.5,
              fontSize: 'var(--t-lead)', fontWeight: 700, flexShrink: 0,
            }}
          >{t('Appliquer')}</button>
          <button
            type="button"
            onClick={() => { setOutilOuvert(false); setPourcent(''); }}
            style={{
              height: 36, padding: '0 13px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
              border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)',
              fontSize: 'var(--t-lead)', fontWeight: 600, flexShrink: 0,
            }}
          >{t('Annuler')}</button>
        </div>
      )}

      <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4 }}>
        {/* ⚠️ `inline-block` + `min-width:100%` (maquette l.3761) : la piste se dimensionne sur son
            CONTENU — donc elle défile — tout en remplissant la largeur disponible quand il y a peu de
            tailles. En bloc simple, les rangées seraient rognées au lieu de faire défiler. */}
        {/* ⚠️ ZONE DE SAISIE EN RAFALE : on remplit une rangee de tailles d’affilee, au clavier ou
            a la douchette. Le retour chariot y avance au champ suivant au lieu de valider. */}
        <div {...{ [SCAN_ZONE_ATTR]: '' }} style={{ display: 'inline-block', minWidth: '100%' }}>
        <div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
          <div style={{ width: 118, flexShrink: 0 }} />
          {grid.sizes.map((size) => (
            <div key={size} style={{
              width: 74, flexShrink: 0, textAlign: 'center', fontFamily: 'var(--font-mono)',
              fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-700)',
            }}>{size}</div>
          ))}
          {/* Colonne de fin : ce que vaut la RANGÉE, et de quoi la propager (relevé l.3767, l.3776). */}
          <div style={{
            width: 150, flexShrink: 0, textAlign: 'center', alignSelf: 'center',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em',
            textTransform: 'uppercase', color: 'var(--ink-500)',
          }}>{t('Ligne')}</div>
        </div>

        {grid.couleurs.map((c) => (
          <div key={c} style={{ display: 'flex', gap: 5, marginBottom: 5, alignItems: 'center' }}>
            <div style={{
              width: 118, flexShrink: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{c}</div>
            {grid.sizes.map((size) => {
              const v = grid.par.get(`${c}|${size}`);
              // ⚠️ Une case sans déclinaison N'EST PAS un champ vide : il n’y a rien à tarifer.
              if (!v) return <div key={size} style={{ width: 74, height: 40, flexShrink: 0 }} />;
              const g = v.gencode;
              const valeur = g && g in saisie ? saisie[g]! : '';
              const enDerogation = deroge(g, v.priceCents);
              const mauvais = !!g && invalides.includes(g);
              return (
                <input
                  key={size}
                  value={valeur}
                  disabled={!editable || !g}
                  onChange={(e) => { if (g) onEdits({ ...saisie, [g]: e.target.value }); }}
                  inputMode="decimal"
                  // ⚠️ LE PLACEHOLDER PORTE LE PRIX EN BASE : c'est ce qui rend « case vide = prix
                  // commun » lisible sans explication, et ce qui montre la dérogation d'un coup d'œil.
                  placeholder={toInputValue(v.priceCents, 2)}
                  title={g ? `${c} / ${size} — ${g}` : t('{couleur} / {taille} — sans code-barres, aucune écriture ne peut l’atteindre', { couleur: c, taille: size })}
                  aria-label={`${fieldLabel('salePriceInclTax')} ${c} ${size}`}
                  style={{
                    width: 74, height: 40, flexShrink: 0, boxSizing: 'border-box',
                    padding: '0 8px', textAlign: 'right', fontSize: 'var(--t-body)', outline: 'none',
                    fontVariantNumeric: 'tabular-nums',
                    borderRadius: 'var(--r-ctrl)',
                    border: `1px solid var(--${mauvais ? 'err' : enDerogation ? 'violet-fg' : 'ink-200'})`,
                    background: g ? (enDerogation ? 'var(--violet-bg)' : 'var(--paper)') : 'var(--ink-50)',
                    color: 'var(--ink-900)',
                  }}
                />
              );
            })}
            <PiedDeRangee
              couleur={c}
              sizes={grid.sizes}
              par={grid.par}
              effectif={effectif}
              editable={editable}
              onDupliquer={(valeur) => {
                // ⚠️ DUPLIQUER REMPLIT LA RANGÉE, il n'écrit pas : comme l'outil de pourcentage, le
                // geste se relit et se corrige avant d'être enregistré.
                const suivant: Record<string, string> = { ...saisie };
                for (const size of grid.sizes) {
                  const v = grid.par.get(`${c}|${size}`);
                  if (v?.gencode) suivant[v.gencode] = valeur;
                }
                onEdits(suivant);
              }}
            />
          </div>
        ))}
        </div>
      </div>

      {/* ⚠️ LA NOTE COMPTE CE QUI DÉROGE, mot pour mot la maquette — c'est la seule façon de savoir,
          sans relire douze cases, si ce modèle a un tarif unique ou des exceptions. */}
      <p style={{ margin: '10px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {nbDerogations === 0
          ? t('Toutes les déclinaisons vendent au prix commun.')
          : t('{n} déclinaison déroge au prix commun — cases violettes. Vider une case la remet au prix commun.', { n: nbDerogations })}
      </p>

      {invalides.length > 0 && (
        <p role="alert" style={{ margin: '8px 0 0', fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
          {t('{n} case illisible — un montant s’écrit « 69,99 ».', { n: invalides.length })}
        </p>
      )}
      {/* ⚠️ PAS DE BOUTON ICI (cohérence 2026-08-24) : les saisies rejoignent la barre
          Annuler/Enregistrer de la FICHE, qui demande confirmation avant d'écrire des prix. */}
      {editable && touche && (
        <p style={{ margin: '8px 0 0', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600 }}>
          {t('{n} case modifiée — l’enregistrement se fait par le bouton de la fiche.', { n: Object.keys(saisie).length })}
        </p>
      )}
    </section>
  );
}

/**
 * LE PIED D'UNE RANGÉE — ce qu'elle vaut, et de quoi la propager.
 *
 * ⚠️ « LIGNE » N'EST PAS UN TOTAL À PAYER : c'est la SOMME des prix unitaires de la rangée. Elle sert à
 * repérer d'un coup d'œil une rangée qui a dérivé — douze tailles au même prix donnent un multiple
 * rond, une valeur qui ne l'est pas signale une exception oubliée. La nommer « total » ferait croire à
 * un panier.
 *
 * ⚠️ « DUPLIQUER » PROPAGE LE PREMIER PRIX de la rangée sur toutes ses tailles. C'est le geste réel du
 * métier : on tarife un coloris, puis on aligne les autres. Il REMPLIT la matrice, il n'écrit pas —
 * même règle que l'outil de pourcentage, pour la même raison (le journal est append-only).
 */
function PiedDeRangee({ couleur, sizes, par, effectif, editable, onDupliquer }: {
  couleur: string;
  sizes: readonly string[];
  par: Map<string, { gencode: string | null; priceCents: number }>;
  effectif: (gencode: string | null, prixBase: number) => number | null;
  editable: boolean;
  onDupliquer: (valeur: string) => void;
}) {
  const prix: number[] = [];
  let premier: number | null = null;
  for (const size of sizes) {
    const v = par.get(`${couleur}|${size}`);
    if (!v) continue;
    const p = effectif(v.gencode, v.priceCents);
    if (p === null) continue;
    if (premier === null) premier = p;
    prix.push(p);
  }
  const sum = prix.reduce((a, b) => a + b, 0);
  // ⚠️ « Toutes au même prix » se DIT : c'est l'information utile, la somme ne la donne pas.
  const uniforme = prix.length > 1 && prix.every((p) => p === prix[0]);

  return (
    <div style={{ width: 150, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
      <span
        title={uniforme ? t('Toutes les tailles de cette couleur sont au même prix.') : undefined}
        style={{
          flex: 1, minWidth: 0, textAlign: 'right', fontSize: 'var(--t-note)',
          fontVariantNumeric: 'tabular-nums',
          color: uniforme ? 'var(--ink-500)' : 'var(--ink-800)',
          fontWeight: uniforme ? 500 : 700,
        }}
      >
        {prix.length === 0 ? '—' : formatMoney(sum, { thousandsSep: ' ' })}
      </span>
      {editable && premier !== null && prix.length > 1 && (
        <button
          type="button"
          className="btn-ghost"
          title={t('Poser {prix} sur toutes les tailles de « {couleur} »', { prix: formatMoney(premier, { thousandsSep: ' ' }), couleur })}
          style={{ height: 28, padding: '0 9px', fontSize: 'var(--t-note)', flexShrink: 0 }}
          onClick={() => onDupliquer(toInputValue(premier, 2))}
        >
          
          {t('Dupliquer')}
        </button>
      )}
    </div>
  );
}

/**
 * L'APERÇU du pourcentage — sur le prix commun RÉEL du modèle.
 * ⚠️ Sur une valeur réelle et pas un exemple : « 100 € → 105 € » rassure sans rien prouver, alors que
 * « 69,99 € → 73,49 € » montre l'arrondi qui sera réellement appliqué.
 */
function Apercu({ prixCommunCents, sens, pourcent }: {
  prixCommunCents: number | null; sens: 'hausse' | 'baisse'; pourcent: string;
}) {
  // ⚠️ L'APERÇU OCCUPE LA PLACE RESTANTE (`flex:1`, min 210 px — maquette l.3757) : sans elle, il se
  // tasse entre le champ et les boutons, et la phrase qui montre l'effet passe à la ligne au milieu.
  const cadre: CSSProperties = {
    flex: 1, minWidth: 210, fontSize: '12.5px', lineHeight: 1.55, paddingBottom: 7,
  };
  // ⚠️ L'APERÇU EMPRUNTE LE CHEMIN DE L'ÉCRITURE, pas un calcul qui lui ressemble : c'est la seule
  //    façon d'être sûr qu'il annonce le prix que le clic produira.
  const facteurBp = facteurPourcentBp(sens, pourcent);
  if (prixCommunCents === null || facteurBp === null) {
    return <div style={{ ...cadre, color: 'var(--ink-400)' }}>{t('Saisissez un pourcentage.')}</div>;
  }
  const after = prixApresPourcentCents(prixCommunCents, facteurBp);
  return (
    <div style={{ ...cadre, color: 'var(--ink-800)', fontWeight: 600 }}>
      {formatMoney(prixCommunCents, { thousandsSep: ' ' })} → {formatMoney(after, { thousandsSep: ' ' })}
    </div>
  );
}
